import type { IconGlyph } from '@sunwebgroup/ui-components/uds'
import { horizontalGap, Modal, SpaceBetween, tk, Typography, verticalGap } from '@sunwebgroup/ui-components'
import { Icon } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'

interface ServiceBoxModalProps {
  closeModal: () => void
  showModal: boolean
  title: string
  description?: string
  descriptionElement?: React.ReactNode
}

interface ServiceBoxProps {
  title: string
  description?: string
  glyph?: IconGlyph
  children?: React.ReactNode
  modalTitle?: string | undefined
  modalDescription?: string | undefined
  modalDescriptionElement?: React.ReactNode | undefined
  modalButtonAriaLabel?: string | undefined
}

const Wrapper = styled.div`
  background: ${tk('color_semantic_bg')};
  box-shadow: ${tk('shadow_lg_bottom')};
  padding: ${tk('spacing_md')};
  border-radius: ${tk('radius_large')};

  @media ${tk('mq_tablet')} {
    padding: calc(${tk('spacing_md')} + ${tk('spacing_2xs')});
  }

  @media ${tk('mq_desktop_md')} {
    padding: ${tk('spacing_lg')};
  }

  ${verticalGap('spacing_sm')}
`

const Heading = styled.div`
  display: flex;
  align-items: center;

  ${horizontalGap('spacing_md')}
`
const Title = styled(Typography)`
  @media (max-width: ${tk('breakpoint_desktop_sm')}) {
    ${Typography.modVariant('body-lg-bold')}
  }
`
export const Description = styled.div`
  ul {
    padding-left: ${tk('spacing_lg')};
  }

  ${Typography} {
    color: ${tk('color_semantic_text')};
  }
`
const Body = styled.div``
const InfoButton = styled.button`
  margin-left: ${tk('spacing_xs')};
  position: relative;
  color: ${tk('color_semantic_icon_strong')};
  ${Icon} {
    vertical-align: middle;
  }
`

export default function ServiceBox({
  children,
  title,
  description,
  glyph,
  modalTitle,
  modalDescription,
  modalDescriptionElement,
  modalButtonAriaLabel,
}: ServiceBoxProps) {
  const [showModal, setShowModal] = React.useState(false)

  const closeModal = () => {
    setShowModal(false)
  }

  const openModal = () => {
    setShowModal(true)
  }
  return (
    <Wrapper data-qa="checkout-service">
      {(title || modalTitle || glyph) && (
        <Heading>
          {glyph && <Icon glyph={glyph} size={24} />}
          <Title as="h3" $variant="heading-md">
            {title}
            {modalTitle && (modalDescription || modalDescriptionElement) && (
              <InfoButton onClick={openModal} aria-label={modalButtonAriaLabel}>
                <Icon glyph="InformationInCircle" size={16} />
              </InfoButton>
            )}
          </Title>
          {modalTitle && (modalDescription || modalDescriptionElement) && (
            <ServiceBoxModal
              title={modalTitle}
              description={modalDescription}
              descriptionElement={modalDescriptionElement}
              closeModal={closeModal}
              showModal={showModal}
            />
          )}
        </Heading>
      )}
      {description && (
        <Description>
          <Typography dangerouslySetInnerHTML={{ __html: description }} />
        </Description>
      )}
      <Body>{children}</Body>
    </Wrapper>
  )
}

function ServiceBoxModal({ title, description, descriptionElement, closeModal, showModal }: ServiceBoxModalProps) {
  return (
    <Modal.Root open={showModal} onClose={closeModal}>
      <Modal.Content $size="md">
        <Modal.Header>
          <Modal.HeaderTitle text={title} />
          <Modal.HeaderCloseButton />
        </Modal.Header>
        <Modal.Body>
          <SpaceBetween
            direction="y"
            justifyContent="space-between"
            spacing={tk('spacing_xl')}
            padding={tk('spacing_xl')}
          >
            {description && <Typography $variant="body-lg" dangerouslySetInnerHTML={{ __html: description }} />}
            {descriptionElement}
          </SpaceBetween>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  )
}
